import React, { useState } from "react";
import axios from "axios";
import "./VisualAcuityTest.css";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const VisualAcuityTest = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [leftEye, setLeftEye] = useState("");
  const [rightEye, setRightEye] = useState("");
  const [bothEyes, setBothEyes] = useState("");

  const [glassesWorn, setGlassesWorn] = useState("");
  const [visionCorrectionType, setVisionCorrectionType] = useState("");
  const [testCondition, setTestCondition] = useState("");
  const [otherLighting, setOtherLighting] = useState("");
  const [testedAt, setTestedAt] = useState("");

  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const downloadCharts = async () => {
  try {
    const zip = new JSZip();

    const basePath = "/Acuity-Charts/Acuity%20Charts/";

   const files = [
  "Far_Acuity_Chart_A4_4m_LandoltC_6_60_to_6_18_Version_1.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_6_60_to_6_18_Version_2.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_6_60_to_6_18_Version_3.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_6_60_to_6_18_Version_4.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_6_60_to_6_18_Version_5.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_Version_1.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_Version_2.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_Version_3.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_Version_4.pdf",
  "Far_Acuity_Chart_A4_4m_LandoltC_Version_5.pdf",
  "readME.pdf",
];

    for (const file of files) {
  const fileUrl = `${basePath}${encodeURIComponent(file)}?v=${Date.now()}`;

  const response = await fetch(fileUrl, {
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`Failed to download: ${file}`);
  }

  const blob = await response.blob();

  zip.file(file, blob);
}
    const content = await zip.generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: {
        level: 6,
      },
    });

    saveAs(content, "Acuity-Charts.zip");
  } catch (error) {
    console.error("ZIP download error:", error);
    alert("Failed to download charts. Please try again.");
  }
};

  /* ================= VISUAL ACUITY OPTIONS ================= */

  const acuityOptions = [
    "6/60",
    "6/36",
    "6/24",
    "6/18",
    "6/12",
    "6/9",
    "6/6",
    "6/5",
    "6/4.5",
    "6/4",
    "6/3.5",
    "6/3",
    "6/2.5",
  ];

  /* ================= SAVE RESULT ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!name.trim() || !email.trim()) {
      setMessage("Name & email required");
      return;
    }

    if (!leftEye || !rightEye || !bothEyes || !testedAt) {
      setMessage(
        "Please fill in all eye results and date/time."
      );
      return;
    }

    if (!glassesWorn) {
      setMessage(
        "Please select whether glasses or contact lenses were worn."
      );
      return;
    }

    if (glassesWorn === "yes" && !visionCorrectionType) {
      setMessage("Please select the type of vision correction.");
      return;
    }

    if (!testCondition) {
      setMessage("Please select the test lighting condition.");
      return;
    }

    if (testCondition === "Other" && !otherLighting.trim()) {
      setMessage("Please specify the other lighting condition.");
      return;
    }

    try {
      setSaving(true);

      await axios.post("/save-free-visual-acuity", {
        name: name.trim(),
        email: email.trim(),
        leftEye,
        rightEye,
        bothEyes,
        glassesWorn,
        visionCorrectionType:
          glassesWorn === "yes" ? visionCorrectionType : "",
        testCondition,
        otherLighting:
          testCondition === "Other"
            ? otherLighting.trim()
            : "",
        testedAt,
      });

      setMessage("Result saved successfully!");

      // Reset fields after successful save
      setName("");
      setEmail("");
      setLeftEye("");
      setRightEye("");
      setBothEyes("");
      setGlassesWorn("");
      setVisionCorrectionType("");
      setTestCondition("");
      setOtherLighting("");
      setTestedAt("");
    } catch (err) {
      console.error("Error saving visual acuity result:", err);

      setMessage(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving result."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="visual-section">
      {/* ================= INTRO ================= */}

      <div className="visual-container">
        <h2 className="visual-title">
          Visual Acuity Assessment
        </h2>

        <p className="visual-subtitle">
          Visual acuity measures how clearly you can see details
          at a specified distance. This assessment helps you record
          your distance-vision results and monitor your visual
          performance.
        </p>

        <hr className="visual-line" />

        <div className="why-card">
          <h3 className="why-title">
            Before You Begin
          </h3>

          <p className="why-text">
            Follow the testing protocol provided for your program.
            Maintain the recommended distance from the visual acuity
            chart and ensure that the chart is clearly visible.
          </p>

          <ul className="why-list">
            <li>
              Maintain the recommended testing distance.
            </li>

            <li>
              Use consistent lighting and avoid glare.
            </li>

            <li>
              Record whether glasses or contact lenses were used.
            </li>
          </ul>
        </div>
      </div>

      {/* ================= MEASUREMENT ================= */}

      <div className="measure-wrapper">
        <div className="measure-card">
          <h2>How to Measure</h2>

          <ul className="measure-list">
            <li>
              Print the chart at <strong>100% size</strong>
            </li>

            <li>
              Stand at <strong>4 meters distance</strong>
            </li>

            <li>
              Cover one eye and read the smallest line
            </li>

            <li>
              Repeat for the other eye and both eyes
            </li>
          </ul>

          <div className="measure-buttons">
            <button
                type="button"
                onClick={downloadCharts}
                className="download-btn"
              >
                Download Chart
              </button>
          </div>

          <hr />

          {/* ================= RESULT FORM ================= */}

          <h3>Enter Your Result</h3>

          <form
            onSubmit={handleSubmit}
            className="result-form"
          >
            {/* NAME */}

            <label>Name *</label>

            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your name"
              required
            />

            {/* EMAIL */}

            <label>Email *</label>

            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />

            {/* LEFT EYE */}

            <label>Left Eye *</label>

            <select
              value={leftEye}
              onChange={(e) => setLeftEye(e.target.value)}
              required
            >
              <option value="">Select</option>

              {acuityOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>

            {/* RIGHT EYE */}

            <label>Right Eye *</label>

            <select
              value={rightEye}
              onChange={(e) => setRightEye(e.target.value)}
              required
            >
              <option value="">Select</option>

              {acuityOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>

            {/* BOTH EYES */}

            <label>Both Eyes *</label>

            <select
              value={bothEyes}
              onChange={(e) => setBothEyes(e.target.value)}
              required
            >
              <option value="">Select</option>

              {acuityOptions.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>

            {/* GLASSES OR CONTACT LENSES */}

            <label>
              Wearing Glasses or Contact Lenses? *
            </label>

            <select
              value={glassesWorn}
              onChange={(e) => {
                setGlassesWorn(e.target.value);
                setVisionCorrectionType("");
              }}
              required
            >
              <option value="">Select</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>

            {/* VISION CORRECTION TYPE */}

            {glassesWorn === "yes" && (
              <>
                <label>
                  Which Vision Correction Was Used? *
                </label>

                <select
                  value={visionCorrectionType}
                  onChange={(e) =>
                    setVisionCorrectionType(e.target.value)
                  }
                  required
                >
                  <option value="">Select</option>
                  <option value="Glasses">Glasses</option>
                  <option value="Contact Lenses">
                    Contact Lenses
                  </option>
                </select>
              </>
            )}

            {/* TEST LIGHTING CONDITION */}

            <label>Test Lighting Condition *</label>

            <select
              value={testCondition}
              onChange={(e) => {
                setTestCondition(e.target.value);
                setOtherLighting("");
              }}
              required
            >
              <option value="">
                Select Lighting Condition
              </option>

              <option value="Indoor Lighting">
                Indoor Lighting
              </option>

              <option value="Outdoor - Direct Sunlight">
                Outdoor - Direct Sunlight
              </option>

              <option value="Outdoor - Shade / Indirect Sunlight">
                Outdoor - Shade / Indirect Sunlight
              </option>

              <option value="Outdoor - Cloudy / Overcast">
                Outdoor - Cloudy / Overcast
              </option>

              <option value="Artificial Lighting">
                Artificial Lighting
              </option>

              <option value="Other">Other</option>
            </select>

            {/* OTHER LIGHTING */}

            {testCondition === "Other" && (
              <>
                <label>
                  Specify Other Lighting Condition *
                </label>

                <input
                  type="text"
                  value={otherLighting}
                  onChange={(e) =>
                    setOtherLighting(e.target.value)
                  }
                  placeholder="Enter lighting condition"
                  required
                />
              </>
            )}

            {/* DATE AND TIME */}

            <label>Date &amp; Time *</label>

            <input
              type="datetime-local"
              value={testedAt}
              onChange={(e) => setTestedAt(e.target.value)}
              className="date-input"
              required
            />

            {/* SUBMIT */}

            <button
              type="submit"
              className="submit-result-btn"
              disabled={saving}
            >
              {saving ? "Saving..." : "Submit Result"}
            </button>
          </form>

          {message && (
            <div
              className="result-message"
              role="status"
            >
              {message}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default VisualAcuityTest;